import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  HeaderTemplate,
  Progress,
  Stepper,
  TitleHeader,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material';
import { MutationStatus } from 'react-query';
import Declaration from '../../../../components/Declaration';
import NavigationButtons from '../../../../components/NavigationButtons';
import RouteKey from '../../../../routes/key';
import ReviewWithdrawalDetails from '../../../../modules/withdrawal/ReviewWithdrawalDetails';
import ReviewWithdrawalCandidateParticulars from '../../../../modules/withdrawal/ReviewWithdrawalCandidateParticulars';
import ReviewWithdrawalExamDetails from './ReviewWithdrawalExamDetails';
import { useGetNonNationalExamWithdrawalReview } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalExamWithdrawalReview';
import { usePutNonNationalConfirmWithdrawal } from '../../../../api-refactored/non-national-exam-registration/usePutNonNationalConfirmWithdrawal';
import PayNowProxyDetailsModal from '../../../../modules/subject/components/SubjectAmendment/PayNowProxyDetailsModal';
import { usePostPayNowProxyDetails } from '../../../../api-refactored/exam-registration/usePostPayNowProxyDetails';

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      left: '-15%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-14%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-13%',
    },
    [theme.breakpoints.down('sm')]: {
      left: '-6%',
    },
  },
  subheader: {
    marginBottom: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
  },
  buttonsContainer: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(7),
  },
  confirmButtonsContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const { examRegistrations, nonNationalReceipt } = RouteKey.withdrawal.child;
const { withdrawal } = RouteKey.child;

const routeSteps = [
  {
    label: 'Reason for Withdrawal',
  },
  {
    label: 'Review Details',
  },
];

const ReviewWithdrawal = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [declErr, setDeclErr] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [errMsg, setErrMsg] = useState<string>();
  const [openPayNowProxyModal, setOpenPayNowProxyModal] = useState(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const { data: withdrawalDetails } = useGetNonNationalExamWithdrawalReview({ uuid });

  const { mutate: mutateNonNationalConfirmWithdrawal } = usePutNonNationalConfirmWithdrawal({
    uuid,
    onSuccess: () =>
      history.push(`/${withdrawal}/${examRegistrations.route}/${uuid}/${nonNationalReceipt.route}`),
    onError: () => {
      setOpenConfirmation(false);
      setErrMsg('Error Encountered');
    },
  });

  const { mutate: postPayNowProxyDetails } = usePostPayNowProxyDetails({
    onSuccess: () => {
      mutateNonNationalConfirmWithdrawal(undefined, {
        onSuccess: () => {
          setApiButtonStatus('success');
          history.push(
            `/${withdrawal}/${examRegistrations.route}/${uuid}/${nonNationalReceipt.route}`
          );
        },
        onError: () => {
          setApiButtonStatus('error');
        },
      });
    },
    onError: () => setApiButtonStatus('error'),
  });

  const handleAgreement = () => {
    setDeclErr(false);
    setIsAgreed(!isAgreed);
  };

  const handleBack = () => history.goBack();

  const handleSubmitApplication = () => {
    if (isAgreed) {
      setOpenConfirmation(true);
    } else {
      setDeclErr(true);
    }
  };

  const confirmationCloseHandler = () => setOpenConfirmation(false);
  const confirmWithdraw = () => {
    if (withdrawalDetails?.payNowProxyRequired) {
      setOpenConfirmation(false);
      setOpenPayNowProxyModal(true);
    } else {
      mutateNonNationalConfirmWithdrawal();
    }
  };

  if (!withdrawalDetails) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <TitleHeader title={t('withdrawal->>WithdrawFromExam.ExamWithdrawal')} />
      <HeaderTemplate>
        <Stepper className={classes.stepper} activeStep={1} steps={routeSteps} />
      </HeaderTemplate>
      <ReviewWithdrawalDetails withdrawalDetails={withdrawalDetails} isSectionHeader />
      <ReviewWithdrawalCandidateParticulars withdrawalDetails={withdrawalDetails} />
      <ReviewWithdrawalExamDetails withdrawalDetails={withdrawalDetails} isDividerBottom />
      <ContentTemplate continuous type="large">
        <Typography variant="h2" className={classes.subheader}>
          {t('withdrawal->>ReviewWithdrawal.Declaration')}
        </Typography>
        <Declaration
          noTitle
          isAgreed={isAgreed}
          handleAgreement={handleAgreement}
          customContent={t('withdrawal->>ReviewWithdrawal.NonNationalDeclarationDescription')}
        />
        {declErr && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {t('withdrawal->>ReviewWithdrawal.DeclarationError')}
            </Typography>
          </Grid>
        )}
        {errMsg && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {errMsg}
            </Typography>
          </Grid>
        )}
        <Grid className={classes.buttonsContainer}>
          <NavigationButtons
            backLabel={t('withdrawal->>ReviewWithdrawal.Back')}
            procceedLabel={t('withdrawal->>ReviewWithdrawal.ConfirmWithdrawal')}
            onBackClick={handleBack}
            onProceedClick={handleSubmitApplication}
          />
        </Grid>
      </ContentTemplate>

      {openConfirmation && (
        <Dialog open={openConfirmation} onClose={confirmationCloseHandler} maxWidth="sm">
          <DialogTitle>{t('withdrawal->>ReviewWithdrawal.ConfirmTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('withdrawal->>ReviewWithdrawal.ConfirmMessage')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box className={classes.confirmButtonsContainer}>
              <Button
                id="button-cancel-withdrawal"
                onClick={confirmationCloseHandler}
                variant="text"
              >
                {t('withdrawal->>ReviewWithdrawal.Cancel')}
              </Button>
              <Button id="button-proceed-withdrawal" type="button" onClick={confirmWithdraw}>
                {t('withdrawal->>ReviewWithdrawal.Proceed')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
      <PayNowProxyDetailsModal
        modalState={openPayNowProxyModal}
        setOpenModalState={setOpenPayNowProxyModal}
        postPayNowProxyDetails={postPayNowProxyDetails}
        applicationUuid={uuid}
        apiButtonStatus={apiButtonStatus}
        setApiButtonStatus={setApiButtonStatus}
      />
    </>
  );
};

export default ReviewWithdrawal;
